import { Gender } from '@pflegenavi/shared/api';
import { useCallback, useMemo, useState } from 'react';
import type { ReceiptBatchRenderRowProps } from '@pflegenavi/shared-web/receipt-batch';
import { ReceiptBatchRowContainer } from '@pflegenavi/shared-web/receipt-batch';
import { useEnqueueSnackbarOnce } from '@pflegenavi/web-components';
import { useInitializeResidents } from '../../hooks/useUnitializeResidents';
import {
  useGetResidentList,
  useUploadReceiptImage,
} from '@pflegenavi/service-provider-frontend/api';
import { v4 } from 'uuid';
import { computeMd5HashOfFile } from '@pflegenavi/shared-web/hash-utils';
import { getGlobalServiceProviderIdFromToken } from '../../utils/getGlobalServiceProviderIdFromToken';
import { useAuthenticationInternal } from '@pflegenavi/frontend/authentication';
import { ServiceProviderReceiptBatchRowContainer } from './form/ServiceProviderReceiptBatchRowContainer';
import type { ServiceProviderReceiptBatchRenderRowProps } from './form/ServiceProviderReceiptBatchReceiptsView';
import { useReceiptBatchStore } from './ReceiptBatchContext';
import { useShallow } from 'zustand/react/shallow';

interface UseServiceProviderRenderReceiptBatchRowResult {
  isLoading: boolean;
  renderRow: (props: ServiceProviderReceiptBatchRenderRowProps) => JSX.Element;
  residents?: Array<{
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    gender: Gender;
  }>;
}

interface UseRenderReceiptBatchRowResult {
  isLoading: boolean;
  renderRow: (props: ReceiptBatchRenderRowProps) => JSX.Element;
  residents?: Array<{
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    gender: Gender;
  }>;
}

export const useServiceProviderRenderReceiptBatchRow =
  (): UseServiceProviderRenderReceiptBatchRowResult => {
    const [
      residents,
      uploadReceiptImage,
      lineItemConfigurations,
      autoGeneratedReceipts,
      isLoading,
    ] = useReceiptBatchStore(
      useShallow((state) => [
        state.residents,
        state.uploadReceiptImage,
        state.lineItemConfigurations,
        state.formik?.values.generateReceipts,
        state.isLoading,
      ])
    );

    const enqueueSnackbarOnce = useEnqueueSnackbarOnce();

    const [showTabTooltip, setShowTabTooltip] = useState(true);

    const renderRow = useCallback(
      // eslint-disable-next-line complexity
      (props: ServiceProviderReceiptBatchRenderRowProps) => {
        const receiptHasResident =
          !!props.receipt.residentId && !!props.receipt.residentName;
        const residentMissing =
          residents.find(
            (resident) => resident.id === props.receipt.residentId
          ) === undefined;
        const allResidents =
          receiptHasResident && residentMissing
            ? [
                ...residents,
                {
                  id: props.receipt.residentId!,
                  name: props.receipt.residentName!,
                  gender: props.receipt.residentGender ?? Gender.Other,
                },
              ]
            : residents;

        return (
          <ServiceProviderReceiptBatchRowContainer
            key={props.receipt.id}
            upload={uploadReceiptImage}
            residents={allResidents}
            lineItemConfigurations={lineItemConfigurations}
            autoGeneratedReceipts={autoGeneratedReceipts ?? false}
            archivedResidents={props.archivedResidents}
            isLoadingResidents={isLoading}
            enqueueSnackbarOnce={enqueueSnackbarOnce}
            showTabTooltip={showTabTooltip}
            setShowTabTooltip={setShowTabTooltip}
            {...props}
          />
        );
      },
      [
        residents,
        uploadReceiptImage,
        lineItemConfigurations,
        autoGeneratedReceipts,
        isLoading,
        enqueueSnackbarOnce,
        showTabTooltip,
      ]
    );

    return {
      isLoading,
      renderRow,
      residents,
    };
  };

/** @deprecated Only to be used when line item feature flag is not enabled */
export const useRenderReceiptBatchRowWithoutLineItems =
  (): UseRenderReceiptBatchRowResult => {
    useInitializeResidents();
    const { getTokenImmediate } = useAuthenticationInternal();
    const globalServiceProviderId = getGlobalServiceProviderIdFromToken(
      getTokenImmediate()
    );

    const selectedNursingHome = useReceiptBatchStore((state) => {
      return state.nursingHomes?.find(
        (n) => n.nursingHomeId === state?.formik?.values?.nursingHomeId
      );
    });

    const { upload } = useUploadReceiptImage({
      tenant: selectedNursingHome?.tenant ?? '',
      uuid: v4,
      computeMd5Hash: computeMd5HashOfFile,
    });

    const { data: residents, isLoading: isLoadingResidents } =
      useGetResidentList(
        {
          nursingHomeId: selectedNursingHome?.nursingHomeId ?? '',
          globalServiceProviderId: globalServiceProviderId!,
        },
        {
          refetchOnMount: false,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
          refetchInterval: 0,
          refetchIntervalInBackground: false,
          enabled:
            !!globalServiceProviderId && !!selectedNursingHome?.nursingHomeId,
        }
      );
    const enqueueSnackbarOnce = useEnqueueSnackbarOnce();

    const [showTabTooltip, setShowTabTooltip] = useState(true);

    const transformedResidents = useMemo(() => {
      return (residents ?? []).map((resident) => ({
        id: resident.id,
        name: `${resident.firstName} ${resident.lastName}`,
        firstName: resident.firstName,
        lastName: resident.lastName,
        gender: resident.gender,
      }));
    }, [residents]);

    const renderRow = useCallback(
      (props: ReceiptBatchRenderRowProps) => {
        const receiptHasResident =
          !!props.receipt.residentId && !!props.receipt.residentName;
        const residentMissing =
          transformedResidents.find(
            (resident) => resident.id === props.receipt.residentId
          ) === undefined;
        const allResidents =
          receiptHasResident && residentMissing
            ? [
                ...transformedResidents,
                {
                  id: props.receipt.residentId!,
                  name: props.receipt.residentName!,
                  gender: props.receipt.residentGender ?? Gender.Other,
                },
              ]
            : transformedResidents;

        return (
          <ReceiptBatchRowContainer
            key={props.receipt.id}
            upload={upload}
            residents={allResidents}
            archivedResidents={props.archivedResidents}
            isLoadingResidents={isLoadingResidents}
            enqueueSnackbarOnce={enqueueSnackbarOnce}
            showTabTooltip={showTabTooltip}
            setShowTabTooltip={setShowTabTooltip}
            autoGeneratedReceipts={false}
            {...props}
          />
        );
      },
      [
        upload,
        transformedResidents,
        isLoadingResidents,
        enqueueSnackbarOnce,
        showTabTooltip,
      ]
    );

    return {
      isLoading: isLoadingResidents,
      renderRow,
      residents: transformedResidents,
    };
  };
